import {DialogContent, DialogTitle, Stack, Typography} from '@mui/material';
import {BaseDialog} from '@resurs-internal/mui';
import {useDialog, useFeatureFlags, useTranslate} from 'hooks';
import {OfferLegalWarning} from './OfferLegalWarning';
import {analytics} from '@resurs-internal/rb-app-api';
import {FontIcon} from 'components';
import {useEffect} from 'react';
import {useNavigate} from '@tanstack/react-router';
import {CustomerOffer} from '@resurs-internal/rb-app-api/features/offers/hooks/types';

export type OfferDialogState = {
  offer: CustomerOffer;
};

export const OfferDialog = () => {
  const translate = useTranslate();
  const dialog = useDialog<OfferDialogState>();
  const offer = dialog.state?.offer;
  const flags = useFeatureFlags();
  const navigate = useNavigate();

  const typeLower = offer?.type.toLowerCase() ?? 'unknown';
  const isInternalLink = offer?.actionType === 'INTERNAL_LINK';
  const showLegalWarning = offer?.showLegalWarning ?? true;

  const onClose = () => {
    analytics.click({
      name: `offer_modal_${typeLower}_close`,
      variantId: flags.discoverV2 ? 'B' : 'A',
    });
    dialog.onClose();
  };
  useEffect(() => {
    if (dialog.state?.type !== 'offer') return;
    const pageId = `offer_modal_${typeLower}`;
    analytics.log('page_view', {
      pageId,
      itemId: offer?.id,
      itemType: typeLower,
      variantId: flags.discoverV2 ? 'B' : 'A',
    });
  }, [dialog.state?.type, typeLower, flags.discoverV2, offer?.id]);

  return (
    <BaseDialog
      open={dialog.state?.type === 'offer'}
      onClose={onClose}
      image={offer?.imageURL}
      actions={[
        {
          title: translate('common.cancel'),
          onClick: onClose,
        },
        {
          title: (
            <Stack direction="row" gap={1}>
              {offer?.dialog?.buttonText}
              {!isInternalLink && (
                <FontIcon icon={'arrow-up-right-from-square'} size={'sm'} />
              )}
            </Stack>
          ),
          color: 'primary',
          onClick: () => {
            analytics.click({
              name: `offer_modal_${typeLower}_link`,
              itemId: offer?.id,
              itemType: typeLower,
              variantId: flags.discoverV2 ? 'B' : 'A',
            });
            if (isInternalLink) {
              navigate({to: offer.webURL});
              dialog.onClose();
            } else {
              window.open(offer?.webURL, '_blank');
            }
          },
        },
      ]}>
      <DialogContent>
        <Stack gap={2} mb={2}>
          <DialogTitle>
            <Typography variant={'h2'} textAlign={'center'}>
              {offer?.dialog?.title}
            </Typography>
          </DialogTitle>
          <Typography
            sx={{whiteSpace: 'pre-line'}}
            variant={'body1'}
            color="secondary">
            {offer?.dialog?.body}
          </Typography>
        </Stack>
        <OfferLegalWarning showLegalWarning={showLegalWarning} />
      </DialogContent>
    </BaseDialog>
  );
};
